import React from "react";
import moment from "moment/moment";
import type { User } from "firebase";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import PaymentStatus from "../components/PaymentStatus";
import PaginationAction from "../components/PaginationAction";
import { useSelector } from "react-redux";
import type { RootState } from "../store/index";
import DatePicker from "../components/DatePicker";
import { formatMoney } from "../utils/number";

interface Props {
  user: User;
  amil_ic: string;
  onClose?: () => void;
}

type Response =
  | { status: "error"; reason: string }
  | { status: "OK"; stats: Stats; data: CollectionFitrah[] };

interface CollectionFitrah {
  id: number;
  datetime: string;
  ref_no: string;
  agency: string;
  currency: string;
  paid_amount: number;
  charge: number;
  amount: number;
  amil_amount: number;
  body_amount: number;
  payer_email: string;
  payer_phone: string;
  payer_ic: string;
  payer_name: string;
  dependant: number;
  rate: number;
  is_approved: boolean;
  reason: string;
  amil_ic: string;
  amil_name: string;
}

interface Form {
  count: number;
  page: number;
  items_per_page: number;
  agency: string;
  start_date: string;
  end_date: string;
  amil: string;
}

interface Stats {
  page: number;
  items_per_page: number;
  count: number;
  total_pages: number;
  paid_amount: number;
  charges: number;
  amount: number;
  amil_amount: number;
  body_amount: number;
}

interface State {
  form: Form;
  stats: Stats;
  report: CollectionFitrah[];
}

type Action =
  | {
      type: "UPDATE_FROM_SERVER";
      report: CollectionFitrah[];
      stats: Stats;
    }
  | { type: "SET_DATE"; start_date: string; end_date: string }
  | { type: "CHANGE_PAGE"; page: number };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "UPDATE_FROM_SERVER":
      const { stats, report } = action;
      const { count, page, items_per_page } = stats;
      return {
        form: {
          ...state.form,
          items_per_page,
          count,
          page,
        },
        report: [...report],
        stats: { ...stats },
      };
    case "CHANGE_PAGE":
      return { ...state, form: { ...state.form, page: action.page } };
    case "SET_DATE":
      const { start_date, end_date } = action;
      return { ...state, form: { ...state.form, start_date, end_date } };
    default:
      throw new Error("no such action");
  }
}

async function fetchUpdate(
  callback: (report: CollectionFitrah[], stats: Stats) => void,
  user: User,
  page: number,
  items: number,
  start_date: string,
  end_date: string,
  download: boolean = false,
  agency: string = "",
  amil: string = ""
) {
  try {
    const token = await user.getIdToken();

    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("items", items.toString());

    if (download) {
      params.set("download", "1");
    }

    if (agency !== "") {
      params.set("agency", agency);
    }

    if (start_date !== "" && end_date !== "") {
      params.set("start_date", start_date);
      params.set("end_date", end_date);
    }

    if (amil !== "") {
      params.set("amil", amil);
    }

    const resp = await fetch(
      `/api/v1/report/collection/fitrah/?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (resp.status === 200) {
      if (download) {
        const data = new Blob([await resp.blob()], { type: "text/csv" });
        const link = window.URL.createObjectURL(data);
        const temp = document.createElement("a");
        temp.href = link;
        temp.setAttribute("download", "fpx_request.csv");
        temp.click();

        window.URL.revokeObjectURL(link);
        return;
      }

      const data = (await resp.json()) as Response;
      if (data.status === "OK") {
        callback(data.data, data.stats);
        return;
      } else if (data.status === "error") {
        throw new Error(data.reason);
      }
    }
    throw new Error("failed to fetch report");
  } catch (e) {
    console.log(e);
  }
}

function AmilCollection({ user, amil_ic, onClose }: Props) {
  const { profile } = useSelector((state: RootState) => state.appState);

  let default_agency = "";
  if (profile) {
    if (profile.access.length === 1) {
      default_agency = profile.access[0];
    }
  }

  const [state, dispatch] = React.useReducer(reducer, {
    form: {
      count: 0,
      page: 0,
      items_per_page: 10,
      agency: default_agency,
      start_date: moment().format("YYYY-MM-DD"),
      end_date: moment().add(1, "day").format("YYYY-MM-DD"),
      amil: amil_ic,
    },
    stats: {
      page: 0,
      items_per_page: 0,
      count: 0,
      total_pages: 0,
      paid_amount: 0,
      charges: 0,
      amount: 0,
      amil_amount: 0,
      body_amount: 0,
    },
    report: [],
  });

  const { form, stats, report } = state;
  const { start_date, end_date, amil } = form;

  const setReport = (report: CollectionFitrah[], stats: Stats) => {
    dispatch({
      type: "UPDATE_FROM_SERVER",
      report,
      stats,
    });
  };

  const update = React.useCallback(() => {
    fetchUpdate(
      setReport,
      user,
      0,
      10,
      start_date,
      end_date,
      false,
      form.agency,
      amil
    );
  }, [amil, end_date, form.agency, start_date, user]);

  React.useEffect(() => {
    update();
  }, [update]);

  const onChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    if (event !== null) {
      fetchUpdate(
        setReport,
        user,
        newPage,
        form.items_per_page,
        start_date,
        end_date,
        false,
        form.agency,
        amil
      );
    }
  };

  const download = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    fetchUpdate(
      setReport,
      user,
      1,
      10,
      start_date,
      end_date,
      true,
      form.agency,
      amil
    );
  };

  const rows = report.map((item: CollectionFitrah) => {
    let amilInfo = null;
    if (item.amil_ic && item.amil_name) {
      amilInfo = (
        <Tooltip title={item.amil_ic}>
          <Typography variant="body1">{item.amil_name}</Typography>
        </Tooltip>
      );
    }

    return (
      <TableRow key={item.id}>
        <TableCell>{item.datetime}</TableCell>
        <TableCell>
          {item.agency} {amilInfo}
        </TableCell>
        <TableCell>
          {item.payer_name && (
            <>
              Pembayar: {item.payer_name}
              <br />
            </>
          )}
          {item.payer_ic && (
            <>
              IC: {item.payer_ic}
              <br />
            </>
          )}
          Tanggungan: {item.dependant}
          <br />
          Kadar Beras: RM {formatMoney(item.rate)}
          <br />
        </TableCell>
        <TableCell align="right">RM {formatMoney(item.amount)}</TableCell>
        <TableCell align="right">RM {formatMoney(item.body_amount)}</TableCell>
        <TableCell align="right">RM {formatMoney(item.amil_amount)}</TableCell>
        <TableCell align="right">
          <PaymentStatus is_approved={item.is_approved} reason={item.reason} />
        </TableCell>
      </TableRow>
    );
  });

  const onDatesChange = (
    startDate: moment.Moment | null,
    endDate: moment.Moment | null
  ) => {
    if (startDate !== null && endDate !== null) {
      dispatch({
        type: "SET_DATE",
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
      });
    }
  };

  return (
    <>
      {onClose != null && (
        <div style={{ float: "left", marginRight: 20 }}>
          <IconButton onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>
        </div>
      )}

      <div style={{ float: "left", marginRight: 20 }}>
        <DatePicker start={start_date} end={end_date} onClick={onDatesChange} />
      </div>

      <Button
        variant="contained"
        color="primary"
        onClick={download}
        style={{ marginTop: 10 }}
      >
        <CloudDownloadIcon />
        <Typography variant="body1" style={{ marginLeft: 10 }}>
          Muat Turun Laporan
        </Typography>
      </Button>

      <Grid container spacing={2} style={{ marginTop: 10 }}>
        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Bayaran
              </Typography>
              <Typography variant="body2" color="textSecondary" component="p">
                {stats.count.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Jumlah Kutipan
              </Typography>
              <Typography variant="body2" color="textSecondary" component="p">
                RM {formatMoney(stats.amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Bahagian Perbadanan
              </Typography>
              <Typography variant="body2" color="textSecondary" component="p">
                RM {formatMoney(stats.body_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Bahagian Amil
              </Typography>
              <Typography variant="body2" color="textSecondary" component="p">
                RM {formatMoney(stats.amil_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PaginationAction
        count={form.count}
        page={form.page}
        rowsPerPage={form.items_per_page}
        onChangePage={onChangePage}
      />

      <TableContainer component={Paper}>
        <Table aria-label="customized table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tarikh</TableCell>
              <TableCell>Amil</TableCell>
              <TableCell>Pembayar</TableCell>
              <TableCell align="right">Jumlah dibayar</TableCell>
              <TableCell align="right">Bahagian Perbadanan</TableCell>
              <TableCell align="right">Bahagian Amil</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default AmilCollection;
